import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { PlatformService } from '@core/platform';
import { VISITOR_ID } from '@core/user-identification';
import { WINDOW } from '@innogy/utils-dom';

import type {
  Alloy,
  AlloyPayload,
  AlloyProposition,
  AlloySendEventResponse,
} from './adobe-web-sdk.model';

export const SDK_INSTANCE_NAME = 'alloy';
declare global {
  interface Window {
    [SDK_INSTANCE_NAME]?: Alloy;
  }
}

/**
 * This service is an adapter for the Adobe Web SDK, providing a purpose-made interface.
 * New usecases of interactions with the SDK can be registered in this service.
 * It takes care of lazy initialization of the SDK and proxies all other calls that outside services need
 * access to.
 */
@Injectable({
  providedIn: 'root',
})
export class AdobeWebSDKService {
  #sdk?: Alloy;

  readonly #window = inject(WINDOW);
  readonly #envConfig = inject(ENVIRONMENT_CONFIG);
  readonly #sdkConfig = this.#envConfig.adobeWebSDK;
  readonly #platform = inject(PlatformService);
  readonly #orgId = '0E57467352783B530A490D45@AdobeOrg';
  readonly #visitorID = inject(VISITOR_ID);

  /**
   * Determines whether the SDK is enabled based on the configuration and platform.
   * @returns A boolean indicating whether the SDK should be enabled.
   */
  public isEnabled(): boolean {
    return (this.#sdkConfig.enabled && this.#platform.isClient()) ?? false;
  }

  /**
   * Initializes the Adobe Web SDK lazily.
   * If the SDK is enabled, it loads and configures the SDK.
   * @returns A promise that resolves to true if initialization is successful, or false if disabled.
   */
  public async init(): Promise<boolean> {
    if (this.isEnabled()) {
      await this.#loadSDK();
      return true;
    }
    return false;
  }

  /**
   * Provides a reference to the Adobe Web SDK instance.
   * Ensures the SDK is loaded before returning it.
   * @returns A promise resolving to the Adobe Web SDK instance.
   */
  public async ref() {
    if (!this.#sdk) {
      await this.#loadSDK();
    }
    return this.#sdk as Alloy;
  }

  /**
   * Private method to load the Adobe Web SDK dynamically.
   * Configures the SDK after it is loaded and links it to the global namespace.
   * @returns A promise that resolves when the SDK is fully loaded and configured.
   */
  async #loadSDK(): Promise<void> {
    if (this.#sdk) {
      return;
    } // Prevent redundant loading
    // We use a CJS import here because adobe does not expose typings and
    // using the MJS version causes immense problems in our project setup;
    // importing this way can localize the references to alloy within this library.
    const { createInstance } = await require('@adobe/alloy');
    this.#sdk = createInstance({ name: SDK_INSTANCE_NAME }) as Alloy;
    await this.#configureSDK();
    await this.#identifyVisitor();
    this.#window[SDK_INSTANCE_NAME] = this.#sdk; // Link SDK to the global window object
  }
  /**
   * Configures the Adobe Web SDK with required settings.
   * Includes options for context, consent, and event handling.
   */
  async #configureSDK() {
    (await this.ref())('configure', {
      personalizationStorageEnabled: false,
      context: ['web', 'device', 'environment', 'placeContext'],
      targetMigrationEnabled: true,
      thirdPartyCookiesEnabled: true,
      idMigrationEnabled: true,
      clickCollectionEnabled: true,
      downloadLinkQualifier:
        '\\.(exe|zip|wav|mp3|mov|mpg|avi|wmv|pdf|doc|docx|xls|xlsx|ppt|pptx)$',
      debugEnabled: false,
      // We are allowed to opt-in to consent for sending events to the adobe Web SDK by default.
      // Filtering of what events are to be forwarded and not to be forwarded is to be done in the edge configuration.
      // @see https://experienceleague.adobe.com/en/docs/experience-platform/web-sdk/commands/setconsent
      // @see https://business.gov.nl/regulation/cookies/
      defaultConsent: 'in',
      datastreamId: this.#sdkConfig.datastreamId,
      orgId: this.#orgId,
      onBeforeEventSend: this.#onBeforeEventSend,
    });
  }

  /**
   * function that identifies a visitor by calling upon the Visitor Identification service
   * of Adobe (visitor.js).
   *
   * We wait for the ID to resolve before continuing execution in order to stabilize the
   * loading order between the Web SDK and visitor.js.
   */
  async #identifyVisitor() {
    try {
      // Wait for the visitorID to resolve before continuing.
      await this.#visitorID;
    } catch (e) {
      throw new Error(`Error attempting to identify visitor: ${e}`);
    }
  }

  /**
   * Event handler for modifying events before they are sent to the Adobe Web SDK.
   * Removes specific referrer URL information to ensure data privacy.
   * @param content - The event content to modify.
   */
  #onBeforeEventSend(content: Record<string, any>): void {
    if (content['xdm'].web?.webReferrer) {
      delete content['xdm'].web.webReferrer.URL;
    }
  }

  public async applyPropositions(payload: AlloyPayload) {
    return (await this.ref())('applyPropositions', payload);
  }

  public async fetchPropositionsForTest(testName: string) {
    return (await this.ref())('sendEvent', {
      renderDecisions: false,
      decisionScopes: [testName],
    }) as Promise<AlloySendEventResponse>;
  }

  public async markPropositionAsDisplayed(payload: AlloyProposition[]) {
    return (await this.ref())('sendEvent', {
      xdm: {
        eventType: 'decisioning.propositionDisplay',
        _experience: {
          decisioning: {
            propositions: payload.map((proposition) => ({
              id: proposition.id,
              scope: proposition.scope,
              scopeDetails: proposition.scopeDetails,
            })),
            propositionEventType: {
              display: 1,
            },
          },
        },
      },
    });
  }
}
